import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Collapse, Dialog, DialogTitle, IconButton, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  BillingPagination,
  ClinicConfig,
  ClinicConfigUpdate,
} from '../../../interfaces/billing.rpm';
import { BillingService } from '../../../services/billing';
import { DEFAULT_PAGE_SIZE } from '../../../util/constants';
import { INITIAL_PAGINATION } from '../types/rpms.types';
import { ClinicConfigurationForm } from './ClinicConfigurationForm';
import { ClinicConfigurationList } from './ClinicConfigurationList';

export const ClinicsConfigurations: React.FC = () => {
  const [openAddClinic, setOpenAddClinic] = useState(false);
  const [editClinic, setEditClinic] = useState(false);
  const [clinicToEdit, setClinicToEdit] = useState<ClinicConfig | undefined>();
  const [clinics, setClinics] = useState<ClinicConfig[]>([]);
  const [pagination, setPagination] = useState<BillingPagination>(INITIAL_PAGINATION);

  const notify = useNotify();

  useEffect(() => {
    fetchClinicsConfiguration();
  }, []);

  const handleClinicFormChange = async (clinicData: ClinicConfig, editing: boolean) => {
    const billingService = new BillingService();

    if (!editing) {
      await billingService.addClinicConfiguration(clinicData);
      setOpenAddClinic(false);
    } else {
      const { id, clinicId, payerId, ...correctData } = clinicData;
      await billingService.updateClinicConfiguration(
        clinicData.id!,
        correctData as ClinicConfigUpdate,
      );

      setEditClinic(false);
      setClinicToEdit(undefined);
    }

    await fetchClinicsConfiguration();
    notify('Configuration change correctly', 'success', {
      autoHideDuration: 30,
    });
  };

  const handleUpdateClinicEnabledState = async (clinicId: number, newState: boolean) => {
    const billingService = new BillingService();
    await billingService.updateClinicConfiguration(clinicId, { rpmEnabled: newState });
    await fetchClinicsConfiguration();
    notify('Configuration change correctly', 'success', {
      autoHideDuration: 30,
    });
  };

  const handleEditClinic = async (configId: number) => {
    setEditClinic(true);

    const clinicFound = clinics.find((clinic) => {
      return clinic.id === configId;
    });

    if (!clinicFound) {
      notify('The clinic selected not exists', 'error', {
        autoHideDuration: 30,
      });

      return;
    }

    setClinicToEdit(clinicFound);
  };

  const onCloseEditDialog = () => {
    setEditClinic(false);
    setClinicToEdit(undefined);
  };

  const fetchClinicsConfiguration = async (page = 1, limit = DEFAULT_PAGE_SIZE) => {
    try {
      const billingService = new BillingService();
      const data = await billingService.getAllClinicsConfiguration(page, limit);
      if (!data) {
        console.error('cant get clinics');
        return;
      }

      setClinics(data.data);
      setPagination(data.pagination);
      setOpenAddClinic(false);
    } catch (error) {
      console.error('Error fetching clinics:', error);
    }
  };

  const handlePageChange = async (newPage: number) => {
    await fetchClinicsConfiguration(newPage, pagination.limit);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    await fetchClinicsConfiguration(1, newLimit);
  };

  return (
    <>
      <Title title='Clinic Configurations' />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '10px',
          marginBottom: '10px',
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='contained' onClick={() => setOpenAddClinic(!openAddClinic)}>
            {openAddClinic ? 'Cancel' : 'Add a new Clinic Configuration'}
          </Button>
        </Box>

        <Collapse in={openAddClinic}>
          <Paper sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
            <ClinicConfigurationForm onChangeFormData={handleClinicFormChange} />
          </Paper>
        </Collapse>
      </Box>

      <ClinicConfigurationList
        clinics={clinics}
        pagination={pagination}
        onSaveClinicEnabledUpdate={handleUpdateClinicEnabledState}
        onEditClinic={handleEditClinic}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={editClinic} onClose={onCloseEditDialog} maxWidth='md' fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            fontSize: '1.5rem',
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          EDIT CLINIC CONFIGURATION
          <IconButton onClick={onCloseEditDialog} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <ClinicConfigurationForm onChangeFormData={handleClinicFormChange} clinic={clinicToEdit} />
      </Dialog>
    </>
  );
};
